import { useReportLostOrStolenCard, useWealthOverview } from '@egw/api'
import { inAppChatService } from '@egw/app-chat'
import { Button, ButtonVariant, ModalDrawer, useModalState } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { ChevronRightIcon } from '@heroicons/react/16/solid'
import { type FC, useState } from 'react'

interface ReportCardAttributeProps {
  cardId?: string
  alias: string
}

export const ReportCardAttribute: FC<ReportCardAttributeProps> = ({ cardId }) => {
  const { t } = useTranslation('features-account')
  const { refetch: refetchCardDetails } = useWealthOverview({ enabled: false })
  const [reportError, setReportError] = useState(false)

  const { isOpen, openModal, closeModal } = useModalState()

  const { mutate: reportCardMutation, isLoading } = useReportLostOrStolenCard({
    onSuccess: (resp) => {
      if (resp.__typename === 'CardStatusUpdateError') {
        setReportError(true)
      } else {
        void refetchCardDetails()
        closeModal()
      }
    },
    onError: () => {
      setReportError(true)
    },
  })

  // -- Callbacks ------------------------------------------------------------
  const reportCardHandler = (): void => {
    sendEvent('user_reportLostStolenDamagedConfirmYes_click', {
      eventSource: 'egWealth',
      category: 'report-card',
      subCategory: 'intent',
    })
    cardId && void reportCardMutation({ cardId })
  }

  const contactSupportHandler = (): void => {
    inAppChatService.open()
    closeModal()
  }

  const bodyContentText = reportError ? t('debit_card.report_card_error') : t('debit_card.report_card_modal_content')

  const btnPrimaryText = reportError ? t('debit_card.contact_client_services') : t('debit_card.report_card_confirm')

  const btnSecondaryText = reportError ? t('debit_card.report_card_error_abort') : t('debit_card.report_card_abort')

  return (
    <>
      <div
        id="reportCardAttribute"
        className="!text-base max-md:px-2 group py-4 border-b transition-all duration-200 cursor-pointer border-light flex justify-between"
        onClick={() => {
          setReportError(false)
          openModal()
          sendEvent('user_reportLostStolenDamaged_click', {
            eventSource: 'egWealth',
            category: 'report-card',
            subCategory: 'intent',
          })
        }}
      >
        {t('debit_card.report_lost_stolen_damaged')}
        <ChevronRightIcon className="fill-iconmuted md:group-hover:fill-iconfull h-6 w-6 stroke-none" />
      </div>

      <ModalDrawer isOpen={isOpen} onClose={closeModal} title={t('debit_card.report_card_modal_title')}>
        <div className="flex flex-col">
          <p className="text-body mb-4 text-left">{bodyContentText}</p>
          {reportError ? (
            <Button
              variant={ButtonVariant.Primary}
              id="contactSupport"
              className="mb-4"
              onClick={contactSupportHandler}
            >
              {btnPrimaryText}
            </Button>
          ) : null}
          {!reportError && (
            <Button
              id="reportCard"
              variant={ButtonVariant.Primary}
              isLoading={isLoading}
              className="mb-4"
              onClick={reportCardHandler}
            >
              {btnPrimaryText}
            </Button>
          )}
          <Button
            variant={ButtonVariant.Secondary}
            id="closeModal"
            onClick={() => {
              sendEvent('user_reportLostStolenDamagedConfirmNo_click', {
                eventSource: 'egWealth',
                category: 'report-card',
                subCategory: 'intent',
              })
              closeModal()
            }}
          >
            {btnSecondaryText}
          </Button>
        </div>
      </ModalDrawer>
    </>
  )
}

export default ReportCardAttribute
